import { CAMERAS_INSTALLED } from '../installed/cameras';
import { SOLAR_INSTALLED } from '../installed/solar';
import { AIR_INSTALLED } from '../installed/air';
import { HEAT_INSTALLED } from '../installed/heat';
import { MUSIC_INSTALLED } from '../installed/music';
import { ASSISTANT_INSTALLED } from '../installed/assistant';
import { PHONE_INSTALLED } from '../installed/phone';

/** IDs de íconos de la barra de navegación inferior. */
export type BottomNavItemId =
  | 'alarm'
  | 'video'
  | 'realtime'
  | 'climate'
  | 'music'
  | 'home'
  | 'widgets'
  | 'reports'
  | 'favorites'
  | 'assistant'
  | 'phone'
  | 'info'
  | 'help';

/** null = ranura vacía (solo multi_row). */
export type BottomNavSlot = BottomNavItemId | null;

export type BottomNavRowsMode = 'collapse' | 'always_two';
export type BottomNavRowsPolicy = 'user_choice' | 'force_two';
export type BottomNavLayoutMode = 'single_row' | 'multi_row';

export const BOTTOM_NAV_MIN_ICONS_PER_ROW = 7;
/** Default histórico; el valor real lo define el admin. */
export const BOTTOM_NAV_ICONS_PER_ROW = 7;
export const BOTTOM_NAV_ROW_PX = 44;
export const BOTTOM_NAV_HANDLE_PX = 18;
/** Padding horizontal de extremos (~8px; antes 1.5≈12px). */
export const BOTTOM_NAV_EDGE_SX = 1.0;

const BOTTOM_NAV_ICON_INSTALLED: Partial<Record<BottomNavItemId, boolean>> = {
  video: CAMERAS_INSTALLED,
  realtime: SOLAR_INSTALLED,
  climate: AIR_INSTALLED || HEAT_INSTALLED,
  music: MUSIC_INSTALLED,
  assistant: ASSISTANT_INSTALLED,
  phone: PHONE_INSTALLED,
};

export const BOTTOM_NAV_FACTORY_ICONS: BottomNavItemId[] = ([
  'alarm',
  'video',
  'realtime',
  'climate',
  'music',
  'home',
  'widgets',
  'reports',
  'favorites',
  'assistant',
  'phone',
  'info',
] as const).filter((id) => BOTTOM_NAV_ICON_INSTALLED[id] !== false);

export const BOTTOM_NAV_FACTORY_ICONS_NO_HELP: BottomNavItemId[] =
  BOTTOM_NAV_FACTORY_ICONS.filter((id) => id !== 'help');

export const BOTTOM_NAV_MAX_ICONS = BOTTOM_NAV_FACTORY_ICONS.length;

export const BOTTOM_NAV_VALID_IDS = new Set<string>(BOTTOM_NAV_FACTORY_ICONS);

/** Compat: orden multi por defecto (7*2 ranuras). */
export const BOTTOM_NAV_TOTAL_SLOTS = BOTTOM_NAV_ICONS_PER_ROW * 2;

export const BOTTOM_NAV_FACTORY_ORDER: BottomNavSlot[] = (() => {
  const slots: BottomNavSlot[] = [...BOTTOM_NAV_FACTORY_ICONS];
  while (slots.length < BOTTOM_NAV_TOTAL_SLOTS) slots.push(null);
  return slots;
})();

export function emptySlotDndId(index: number): string {
  return `__empty_${index}`;
}

export function slotDndId(index: number, slot: BottomNavSlot): string {
  return slot ?? emptySlotDndId(index);
}

export function clampIconsPerRow(n: number, maxIcons = BOTTOM_NAV_MAX_ICONS): number {
  const mx = Math.max(BOTTOM_NAV_MIN_ICONS_PER_ROW, maxIcons);
  return Math.max(BOTTOM_NAV_MIN_ICONS_PER_ROW, Math.min(mx, Math.floor(n) || BOTTOM_NAV_MIN_ICONS_PER_ROW));
}

/** Asegura que `info` quede último entre los íconos visibles (antes de `help` si existe). */
export function pinInfoLast(slots: BottomNavSlot[]): BottomNavSlot[] {
  const infoIdx = slots.findIndex((s) => s === 'info');
  if (infoIdx < 0) return slots;
  const withoutInfo = slots.filter((s) => s !== 'info');
  const helpIdx = withoutInfo.findIndex((s) => s === 'help');
  if (helpIdx >= 0) {
    const next = [...withoutInfo];
    next.splice(helpIdx, 0, 'info');
    return next.length === slots.length
      ? next
      : (() => {
          while (next.length < slots.length) next.push(null);
          return next.slice(0, slots.length);
        })();
  }
  // Lista compacta (single_row): info al final
  if (withoutInfo.every((s) => s != null)) {
    return [...withoutInfo, 'info'];
  }
  // multi_row con huecos: insertar info justo después del último ícono ocupado
  const lastFilled = withoutInfo.reduce((acc, s, i) => (s != null ? i : acc), -1);
  const next = [...withoutInfo];
  next.splice(lastFilled + 1, 0, 'info');
  while (next.length > slots.length) {
    const empty = next.findIndex((s) => s === null);
    if (empty < 0) {
      next.pop();
      break;
    }
    next.splice(empty, 1);
  }
  while (next.length < slots.length) next.push(null);
  return next.slice(0, slots.length);
}

export function normalizeBottomNavOrder(
  raw: unknown,
  opts?: { layoutMode?: BottomNavLayoutMode; iconsPerRow?: number },
): BottomNavSlot[] {
  const layoutMode = opts?.layoutMode ?? 'single_row';
  const iconsPerRow = clampIconsPerRow(opts?.iconsPerRow ?? BOTTOM_NAV_ICONS_PER_ROW);

  let list: unknown[] = [];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) list = parsed;
    } catch {
      list = [];
    }
  } else if (Array.isArray(raw)) {
    list = raw;
  }

  if (layoutMode === 'single_row') {
    const seen = new Set<string>();
    const result: BottomNavSlot[] = [];
    for (const item of list) {
      if (item == null || item === '' || item === 'null') continue;
      if (typeof item !== 'string') continue;
      const key = item.trim();
      if (key === 'help') continue;
      if (BOTTOM_NAV_VALID_IDS.has(key) && !seen.has(key)) {
        seen.add(key);
        result.push(key as BottomNavItemId);
      }
    }
    for (const key of BOTTOM_NAV_FACTORY_ICONS_NO_HELP) {
      if (!seen.has(key)) {
        result.push(key);
        seen.add(key);
      }
    }
    return pinInfoLast(result);
  }

  const totalSlots = iconsPerRow * 2;
  const slots: BottomNavSlot[] = Array.from({ length: totalSlots }, () => null);
  const seen = new Set<string>();
  const max = Math.min(list.length, totalSlots);
  for (let i = 0; i < max; i++) {
    const item = list[i];
    if (item == null || item === '' || item === 'null') {
      slots[i] = null;
      continue;
    }
    if (typeof item !== 'string') continue;
    const key = item.trim();
    if (key === 'help') continue;
    if (BOTTOM_NAV_VALID_IDS.has(key) && !seen.has(key)) {
      seen.add(key);
      slots[i] = key as BottomNavItemId;
    }
  }
  for (const key of BOTTOM_NAV_FACTORY_ICONS) {
    if (seen.has(key)) continue;
    const free = slots.findIndex((s) => s === null);
    if (free < 0) break;
    slots[free] = key;
    seen.add(key);
  }
  return pinInfoLast(slots);
}

export function computeBottomNavHeightPx(opts: {
  needsTwoRows: boolean;
  expanded: boolean;
  showHandle: boolean;
  singleRow?: boolean;
}): number {
  const { needsTwoRows, expanded, showHandle, singleRow = false } = opts;
  if (singleRow) return BOTTOM_NAV_ROW_PX;
  const rows = needsTwoRows && expanded ? 2 : 1;
  return (showHandle ? BOTTOM_NAV_HANDLE_PX : 0) + rows * BOTTOM_NAV_ROW_PX;
}

/** Altura real de la barra; la publica `BottomNavBar`. Fallback si aún no está. */
export const SHC_BOTTOM_NAV_HEIGHT_VAR = '--shc-bottom-nav-height';
export const SHC_BOTTOM_NAV_CLEARANCE = `var(${SHC_BOTTOM_NAV_HEIGHT_VAR}, 110px)`;

export function publishBottomNavHeightPx(heightPx: number | null): void {
  if (typeof document === 'undefined') return;
  if (heightPx == null) {
    document.documentElement.style.removeProperty(SHC_BOTTOM_NAV_HEIGHT_VAR);
    return;
  }
  document.documentElement.style.setProperty(SHC_BOTTOM_NAV_HEIGHT_VAR, `${Math.max(0, Math.round(heightPx))}px`);
}

export function readBottomNavClearancePx(fallback = 110): number {
  if (typeof document === 'undefined') return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(SHC_BOTTOM_NAV_HEIGHT_VAR).trim();
  const n = parseFloat(raw);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

/** Caché local para pintar la barra con la última config conocida (evita flash multi→single). */
export interface BottomNavPrefsCache {
  userId: number | string;
  layout_mode: BottomNavLayoutMode;
  icons_per_row: number;
  order: BottomNavSlot[];
  rows_mode: BottomNavRowsMode;
  rows_mode_locked: boolean;
  user_reorder_allowed: boolean;
}

/** v3: info siempre último; invalida órdenes cacheados con climate/widgets tras info. */
const BOTTOM_NAV_PREFS_CACHE_KEY = 'shc_bottom_nav_prefs_v3';

export function readBottomNavPrefsCache(
  userId: number | string | undefined | null,
): BottomNavPrefsCache | null {
  if (userId == null || userId === '') return null;
  try {
    // Limpiar claves antiguas
    try {
      localStorage.removeItem('shc_bottom_nav_prefs_v1');
      localStorage.removeItem('shc_bottom_nav_prefs_v2');
    } catch {
      /* ignore */
    }
    const raw = localStorage.getItem(BOTTOM_NAV_PREFS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BottomNavPrefsCache;
    if (String(parsed.userId) !== String(userId)) return null;
    if (parsed.layout_mode !== 'single_row' && parsed.layout_mode !== 'multi_row') return null;
    if (!Array.isArray(parsed.order)) return null;
    const iconsPerRow = clampIconsPerRow(parsed.icons_per_row ?? BOTTOM_NAV_ICONS_PER_ROW);
    return {
      ...parsed,
      icons_per_row: iconsPerRow,
      order: normalizeBottomNavOrder(parsed.order, {
        layoutMode: parsed.layout_mode,
        iconsPerRow,
      }),
    };
  } catch {
    return null;
  }
}

export function writeBottomNavPrefsCache(cache: BottomNavPrefsCache): void {
  try {
    localStorage.setItem(BOTTOM_NAV_PREFS_CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* quota / private mode */
  }
}
